import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import { getVisitsByProject } from '../../api/supervisor';
import type { SupervisorVisit } from '../../api/supervisor';
import { secondaryColors, successColors } from '../../theme/themeConfig';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(minutes?: number): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short'
  });
}

function isMissed(v: SupervisorVisit): boolean {
  return v.status !== 'completed' && v.status !== 'in_progress';
}

function VisitStatusIcon({ v }: { v: SupervisorVisit }) {
  const missed = isMissed(v);
  if (v.status === 'completed')
    return (
      <CheckCircleOutlineIcon
        sx={{ color: successColors.SUCCESS, fontSize: 15 }}
      />
    );
  if (missed)
    return <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 15 }} />;
  return <AccessTimeIcon sx={{ color: '#F59E0B', fontSize: 15 }} />;
}

// ── Visit log dialog ──────────────────────────────────────────────────────────

function VisitLogDialog({
  onClose,
  visits
}: {
  onClose: () => void;
  visits: SupervisorVisit[];
}) {
  return (
    <Dialog fullWidth open maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Typography fontSize={16} fontWeight={700}>
          Supervisor Visit History
        </Typography>
        <Typography color={secondaryColors.SECONDARY} fontSize={13}>
          {visits.length} visit{visits.length !== 1 ? 's' : ''}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visits.map((visit) => {
            const missed = isMissed(visit);
            return (
              <Box
                key={visit.id}
                sx={{
                  background: missed ? '#FEF2F2' : '#F8FAFC',
                  border: `1px solid ${missed ? '#FECACA' : '#E2E8F0'}`,
                  borderRadius: '10px',
                  p: '12px 14px'
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5
                  }}
                >
                  <Box
                    sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}
                  >
                    <VisitStatusIcon v={visit} />
                    <Typography fontSize={13} fontWeight={600}>
                      {visit.supervisor?.firstName} {visit.supervisor?.lastName}
                    </Typography>
                  </Box>
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                    <Typography color={secondaryColors.SECONDARY} fontSize={12}>
                      {formatDuration(visit.durationMinutes)}
                    </Typography>
                    {visit.inspectionId && (
                      <Chip
                        label="Inspection"
                        size="small"
                        sx={{
                          background: '#EEF2FF',
                          color: '#534AB7',
                          fontSize: 9,
                          height: 16
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Typography color={secondaryColors.SECONDARY} fontSize={11}>
                  {formatDateTime(visit.checkedInAt ?? visit.createdAt)}
                  {visit.checkedOutAt &&
                    ` — ${formatDateTime(visit.checkedOutAt)}`}
                </Typography>
                {visit.notes && (
                  <Typography fontSize={12} mt={0.5} sx={{ color: '#374151' }}>
                    {visit.notes}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

interface Props {
  projectId: string;
}

const SupervisorActivityPanel: React.FC<Props> =
  function SupervisorActivityPanel({ projectId }) {
    const [visits, setVisits] = useState<SupervisorVisit[]>([]);
    const [loading, setLoading] = useState(true);
    const [showLog, setShowLog] = useState(false);

    useEffect(() => {
      getVisitsByProject(projectId)
        .then(setVisits)
        .catch(() => setVisits([]))
        .finally(() => setLoading(false));
    }, [projectId]);

    const completed = visits.filter((v) => v.status === 'completed');
    const totalMinutes = completed.reduce(
      (sum, v) => sum + (v.durationMinutes ?? 0),
      0
    );
    const lastVisit = completed[0] ?? null;
    const missedCount = visits.filter(isMissed).length;

    return (
      <>
        <Box sx={{ borderBottom: '1px solid #E2E4EA', p: '14px 16px' }}>
          {/* Section label */}
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }}
          >
            <Typography
              sx={{
                color: '#7D8090',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}
            >
              Supervisor Activity
            </Typography>
            {visits.length > 0 && (
              <IconButton
                onClick={() => setShowLog(true)}
                size="small"
                sx={{ color: secondaryColors.SECONDARY, p: '2px' }}
                title="View all visits"
              >
                <LaunchOutlinedIcon sx={{ fontSize: 13 }} />
              </IconButton>
            )}
          </Box>

          {loading && <CircularProgress size={14} />}

          {!loading && visits.length === 0 && (
            <Typography
              color={secondaryColors.SECONDARY}
              fontSize={12}
              sx={{ fontStyle: 'italic' }}
            >
              No visits yet
            </Typography>
          )}

          {!loading && visits.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {/* Summary row */}
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
                <Box>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={10}
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '.4px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Total
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {formatDuration(totalMinutes)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={10}
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '.4px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Visits
                  </Typography>
                  <Typography fontSize={13} fontWeight={700}>
                    {completed.length}
                  </Typography>
                </Box>
                {missedCount > 0 && (
                  <Box>
                    <Typography
                      color={secondaryColors.SECONDARY}
                      fontSize={10}
                      sx={{
                        fontWeight: 600,
                        letterSpacing: '.4px',
                        textTransform: 'uppercase'
                      }}
                    >
                      Missed
                    </Typography>
                    <Typography
                      fontSize={13}
                      fontWeight={700}
                      sx={{ color: '#EF4444' }}
                    >
                      {missedCount}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Last visit */}
              {lastVisit && (
                <Box
                  sx={{
                    background: '#F8FAFC',
                    borderRadius: '8px',
                    p: '8px 10px'
                  }}
                >
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={10}
                    mb={0.3}
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '.4px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Last Visit
                  </Typography>
                  <Typography fontSize={12} fontWeight={600}>
                    {formatDateShort(
                      lastVisit.checkedInAt ?? lastVisit.createdAt
                    )}
                    <Box
                      component="span"
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontWeight: 400,
                        ml: 0.5
                      }}
                    >
                      · {formatDuration(lastVisit.durationMinutes)}
                    </Box>
                  </Typography>
                  {lastVisit.notes && (
                    <Typography
                      color={secondaryColors.SECONDARY}
                      fontSize={11}
                      mt={0.3}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {lastVisit.notes}
                    </Typography>
                  )}
                  {lastVisit.inspectionId && (
                    <Chip
                      label="Inspection conducted"
                      size="small"
                      sx={{
                        background: '#EEF2FF',
                        color: '#534AB7',
                        fontSize: 9,
                        height: 16,
                        mt: 0.3
                      }}
                    />
                  )}
                </Box>
              )}

              <Typography
                color={secondaryColors.SECONDARY}
                fontSize={11}
                onClick={() => setShowLog(true)}
                sx={{ cursor: 'pointer', mt: 0.3, textDecoration: 'underline' }}
              >
                View all {visits.length} visit{visits.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Box>

        {showLog && (
          <VisitLogDialog onClose={() => setShowLog(false)} visits={visits} />
        )}
      </>
    );
  };

export default SupervisorActivityPanel;
